import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  Plus,
  Pencil,
  Trash2,
  Server as ServerIcon,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import {
  serversApi,
  groupsApi,
  environmentsApi,
  Server,
  ServerComponent,
} from "@/api/client";
import EnvironmentSelect from "@/components/EnvironmentSelect";
import { titleCase } from "@/lib/utils";

export default function Servers() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showForm, setShowForm] = useState(false);
  const [editingServer, setEditingServer] = useState<Server | null>(null);
  const [expandedServer, setExpandedServer] = useState<string | null>(null);
  const [envFilter, setEnvFilter] = useState<string>("");
  const [groupFilter, setGroupFilter] = useState<string>(
    searchParams.get("group") || "",
  );

  const queryClient = useQueryClient();

  // Sync group filter with URL
  useEffect(() => {
    const urlGroup = searchParams.get("group") || "";
    if (urlGroup !== groupFilter) {
      setGroupFilter(urlGroup);
    }
  }, [searchParams]);

  const { data: serversData, isLoading } = useQuery({
    queryKey: ["servers", envFilter, groupFilter],
    queryFn: () =>
      serversApi.list({
        environment_id: envFilter || undefined,
        group_id: groupFilter || undefined,
      }),
  });

  const { data: groupsData } = useQuery({
    queryKey: ["groups"],
    queryFn: () => groupsApi.list(),
  });

  const { data: envsData } = useQuery({
    queryKey: ["environments"],
    queryFn: () => environmentsApi.list(),
  });

  const { data: serverDetails } = useQuery({
    queryKey: ["server", expandedServer],
    queryFn: () => (expandedServer ? serversApi.get(expandedServer) : null),
    enabled: !!expandedServer,
  });

  const createMutation = useMutation({
    mutationKey: ["createServer"],
    mutationFn: serversApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["servers"] });
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationKey: ["updateServer"],
    mutationFn: ({
      id,
      ...data
    }: { id: string } & Parameters<typeof serversApi.update>[1]) =>
      serversApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["servers"] });
      setEditingServer(null);
    },
  });

  const deleteMutation = useMutation({
    mutationKey: ["deleteServer"],
    mutationFn: serversApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["servers"] });
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const serverData = {
      hostname: formData.get("hostname") as string,
      environment_id: formData.get("environment_id") as string,
      group_id: (formData.get("group_id") as string) || undefined,
      ip_address: (formData.get("ip_address") as string) || undefined,
      description: (formData.get("description") as string) || undefined,
      is_active: formData.get("is_active") === "on",
    };

    if (editingServer) {
      updateMutation.mutate({ id: editingServer.id, ...serverData });
    } else {
      createMutation.mutate(serverData);
    }
  };

  const handleEdit = (server: Server) => {
    setEditingServer(server);
    setShowForm(true);
  };

  const handleDelete = (id: string, hostname: string) => {
    if (
      confirm(
        `Are you sure you want to delete server "${hostname}"? This will remove all component assignments.`,
      )
    ) {
      deleteMutation.mutate(id);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingServer(null);
  };

  const toggleExpand = (serverId: string) => {
    setExpandedServer(expandedServer === serverId ? null : serverId);
  };

  const servers = serversData?.servers || [];
  const groups = groupsData?.groups || [];
  const environments = envsData?.environments || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Servers</h1>
          <p className="text-gray-600">
            Manage servers and their component assignments
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <Plus size={20} />
          Add Server
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <EnvironmentSelect value={envFilter} onChange={setEnvFilter} />
        <select
          value={groupFilter}
          onChange={(e) => {
            const value = e.target.value;
            setGroupFilter(value);
            if (value) {
              setSearchParams((prev) => {
                prev.set("group", value);
                return prev;
              });
            } else {
              setSearchParams((prev) => {
                prev.delete("group");
                return prev;
              });
            }
          }}
          className="border rounded-lg px-3 py-2"
        >
          <option value="">All Groups</option>
          {groups.map((group) => (
            <option key={group.id} value={group.slug}>
              {group.name}
            </option>
          ))}
        </select>
      </div>

      {/* Form */}
      {(showForm || editingServer) && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">
            {editingServer ? "Edit Server" : "Add Server"}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hostname *
                </label>
                <input
                  name="hostname"
                  type="text"
                  required
                  defaultValue={editingServer?.hostname}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="web-server-01.example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  IP Address
                </label>
                <input
                  name="ip_address"
                  type="text"
                  defaultValue={editingServer?.ip_address}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="192.168.1.100"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Environment *
                </label>
                <select
                  name="environment_id"
                  required
                  defaultValue={editingServer?.environment_id}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="">Select environment...</option>
                  {environments.map((env) => (
                    <option key={env.id} value={env.id}>
                      {env.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Group
                </label>
                <select
                  name="group_id"
                  defaultValue={editingServer?.group_id || ""}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="">No group</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                defaultValue={editingServer?.description}
                className="w-full border rounded-lg px-3 py-2"
                rows={2}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                name="is_active"
                id="is_active"
                defaultChecked={editingServer?.is_active ?? true}
              />
              <label htmlFor="is_active" className="text-sm text-gray-700">
                Server is active
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {editingServer ? "Update" : "Create"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Servers List */}
      {isLoading ? (
        <div className="text-center py-8 text-gray-500">Loading...</div>
      ) : servers.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No servers found. Add your first server to get started.
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-8"></th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Hostname
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Environment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Components
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Drift
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {servers.map((server) => (
                <React.Fragment key={server.id}>
                  <tr
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => toggleExpand(server.id)}
                  >
                    <td className="pl-4">
                      {expandedServer === server.id ? (
                        <ChevronDown size={16} className="text-gray-400" />
                      ) : (
                        <ChevronRight size={16} className="text-gray-400" />
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <ServerIcon size={16} className="text-gray-400" />
                        <div>
                          <div className="font-medium text-gray-900">
                            {server.hostname}
                          </div>
                          {server.ip_address && (
                            <div className="text-sm text-gray-500">
                              {server.ip_address}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-900">
                        {server.environment_name}
                      </span>
                      {server.group_name && (
                        <span className="ml-2 text-xs text-gray-500">
                          ({server.group_name})
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      {server.component_count || 0}
                    </td>
                    <td className="px-6 py-4">
                      {(server.drift_count || 0) > 0 ? (
                        <span className="flex items-center gap-1 text-red-600">
                          <AlertTriangle size={16} />
                          {server.drift_count}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-green-600">
                          <CheckCircle size={16} />
                          OK
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex px-2 py-1 text-xs rounded-full ${
                          server.is_active
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {server.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(server);
                        }}
                        className="text-blue-600 hover:text-blue-800 mr-3"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(server.id, server.hostname);
                        }}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                  {expandedServer === server.id && serverDetails && (
                    <tr>
                      <td colSpan={7} className="bg-gray-50 px-6 py-4">
                        <div className="text-sm">
                          <h4 className="font-medium mb-2">Components</h4>
                          {serverDetails.components.length === 0 ? (
                            <p className="text-gray-500">
                              No components assigned
                            </p>
                          ) : (
                            <table className="min-w-full">
                              <thead>
                                <tr className="text-xs text-gray-500">
                                  <th className="text-left py-1">Component</th>
                                  <th className="text-left py-1">Service</th>
                                  <th className="text-left py-1">Expected</th>
                                  <th className="text-left py-1">Actual</th>
                                  <th className="text-left py-1">Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {serverDetails.components.map(
                                  (comp: ServerComponent) => (
                                    <tr key={comp.id}>
                                      <td className="py-1">
                                        {comp.component_name}
                                      </td>
                                      <td className="py-1 text-gray-500">
                                        {titleCase(comp.service_name)}
                                      </td>
                                      <td className="py-1 font-mono text-xs">
                                        {comp.expected_version || "-"}
                                      </td>
                                      <td className="py-1 font-mono text-xs">
                                        {comp.actual_version || "-"}
                                      </td>
                                      <td className="py-1">
                                        {comp.is_drift ? (
                                          <span className="text-red-600 flex items-center gap-1">
                                            <AlertTriangle size={14} />
                                            Drift
                                          </span>
                                        ) : (
                                          <span className="text-green-600 flex items-center gap-1">
                                            <CheckCircle size={14} />
                                            OK
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                  ),
                                )}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
